import { Box } from "@mui/material";
import Alert from "@mui/material/Alert";
import { DualListItem } from "components/DualListSelector";
import DualListSelectorSearchableForm from "components/DualListSelector/DualListSelectorSearchableForm";
import Loader from "components/Loader";
import SnackbarMessage from "components/SnackbarMessage";
import useGlobalContext from "hooks/globalData";
import useTrashablePromises from "hooks/useTrashablePromises";
import { responseError, skuRuleAvailableTagsKey } from "pages/SkusPage/InternalTags/skuTags";
import { useCallback, useEffect, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import {
  AdminListRequestOfBaseFilter,
  BaseFilter,
  ListRequestOfBaseFilter,
  SkuContentRule,
  SkuContentRuleTag,
  Skus,
  Tag,
} from "services/KnowfullyAdminClient";
import { SnackbarMessageProps } from "types/common-types";
import { SkuFormProps } from "types/sku-related-types";

type SkuRuleDetailTagFormProps = {
  sku: Skus;
  skuContentRule: SkuContentRule;
  readonly?: boolean;
  index: number;
} & SkuFormProps;

const mapTags = (tags: Tag[]) => tags.map((tag) => new DualListItem(tag.id, tag.name));

const mapSkuContentRuleTags = (ruleTags: SkuContentRuleTag[]) =>
  ruleTags.map((ruleTag) => new DualListItem(ruleTag.tagId, ruleTag.name));

const SkuRuleDetailTagForm = ({
  sku,
  skuContentRule,
  readonly = false,
  index,
  onDirty,
  onClean,
  onSkuUpdated,
}: SkuRuleDetailTagFormProps) => {
  const { formatMessage: t } = useIntl();
  const { registerPromise } = useTrashablePromises();
  const queryClient = useQueryClient();
  const { skuService } = useGlobalContext();
  const [snackbarMessage, setSnackbarMessage] = useState<SnackbarMessageProps | null>(null);
  const [options, setOptions] = useState<DualListItem[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<DualListItem[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  const skuContentRuleTagsResult = useQuery(["skuRuleDetails", sku.id, skuContentRule.id], () =>
    skuService.getSkuContentRuleTags(
      sku.id!,
      skuContentRule.id,
      AdminListRequestOfBaseFilter.fromJS({ sortField: "tag.name" })
    )
  );

  const fetchedTags = useQuery([skuRuleAvailableTagsKey, sku.id, searchTerm], () =>
    skuService.getAvailableRuleTags(
      sku.id!,
      ListRequestOfBaseFilter.fromJS({
        pageSize: -1,
        parameters: BaseFilter.fromJS({
          searchText: searchTerm === "" ? undefined : searchTerm,
        }),
      })
    )
  );

  useEffect(() => {
    setOptions(fetchedTags.data?.data ? mapTags(fetchedTags.data.data) : []);
  }, [fetchedTags.data]);

  useEffect(() => {
    setSelectedOptions(
      skuContentRuleTagsResult.data?.data ? mapSkuContentRuleTags(skuContentRuleTagsResult.data.data) : []
    );
  }, [skuContentRuleTagsResult.data]);

  const onCleanForm = useCallback(() => {
    setSelectedOptions(
      skuContentRuleTagsResult.data?.data ? mapSkuContentRuleTags(skuContentRuleTagsResult.data.data) : []
    );

    onClean && onClean();
  }, [skuContentRuleTagsResult.data, onClean]);

  if (skuContentRuleTagsResult.isLoading) return <Loader />;
  if (skuContentRuleTagsResult.isError) {
    return (
      <Box mt={4}>
        <Alert severity="error">
          <T id="skuRuleDetailsError" />
        </Alert>
      </Box>
    );
  }

  const onTagSelectionChange = (selected: DualListItem[]) => {
    selected.sort((a, b) => a.text.localeCompare(b.text));
    setSelectedOptions(selected);
  };

  // DualListSelectorSearchableForm shows an error only when the submit promise rejects.
  const onSubmit = async () => {
    const failure = responseError(
      await registerPromise(
        skuService.saveSkuContentRuleTags(
          sku.id!,
          skuContentRule.id,
          selectedOptions.map((s) => s.id)
        )
      ),
      t({ id: "serverError" })
    );
    if (failure) {
      throw new Error(failure);
    }

    queryClient.invalidateQueries(["skuRuleDetails", sku.id, skuContentRule.id]);

    onSkuUpdated && onSkuUpdated();
    setSnackbarMessage(new SnackbarMessageProps(t({ id: "ruleUpdatedMessage" })));
  };

  const availableTagsFailed =
    fetchedTags.isError || (fetchedTags.data !== undefined && responseError(fetchedTags.data, "") !== undefined);

  return (
    <>
      {availableTagsFailed && (
        <Box mt={2}>
          <Alert severity="error">
            <T id="skuRuleDetailsError" />
          </Alert>
        </Box>
      )}
      <DualListSelectorSearchableForm
        setSearchTerm={setSearchTerm}
        options={options}
        selectedOptions={selectedOptions}
        isFetching={fetchedTags.isFetching}
        readonly={readonly}
        index={index}
        onSubmit={onSubmit}
        onChange={onTagSelectionChange}
        onClean={onCleanForm}
        onDirty={onDirty}
      />
      <SnackbarMessage snackbarMessage={snackbarMessage} setSnackbarMessage={setSnackbarMessage} />
    </>
  );
};

export default SkuRuleDetailTagForm;
